/**
 * history.js
 */

import React from 'react';
import { useMediaQuery, Container, Box, Link, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { makeLocation } from 'utils/routing';
import useUser from 'utils/use-user';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import PageTitle from 'components/page-title';
import PageTitleForUser from 'components/page-title-for-user';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import DataGrid from 'components/data-grid';


const useStyles = makeStyles(theme => ({
  grid: {
    '& > *': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      '& > *': {
        flex: 1
      }
    },
    '& .MuiDataGrid-root': {
      [theme.breakpoints.down('sm')]: {
        border: 0
      }
    },
    '& .MuiDataGrid-columnHeaders': {
      '& .MuiDataGrid-columnHeadersInner': {
        paddingLeft: 4,
        flex: 1,
      }, 
      '& .MuiDataGrid-columnHeader': {
        padding: `0 4px`,
      },
      '& .MuiDataGrid-columnHeaderTitleContainer': {
        padding: `0 0px`,
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: theme.typography.sizes[1],
        lineHeight: '14px',
      },
      '& .MuiIconButton-sizeSmall': {
        padding: 0,
      },
    },
    '& .MuiDataGrid-row': {
      padding: `0 ${theme.spacing(0.5)}`,
      '& .MuiDataGrid-cell': {
        padding: '0 4px',
        [theme.breakpoints.down('sm')]: {
          fontSize: theme.typography.sizes[2],
        },
        '& span, & a': {
          overflow: 'inherit',
          textOverflow: 'inherit',
        },
        '&.date': {
          whiteSpace: 'normal',
        },
        '&.icon-link': {
          lineHeight: `1.2 !important`,
          marginLeft: 2,
        },
        '& .date-wrap': {
          lineHeight: `1.2 !important`,
        },
        '& .no-wrap': {
          whiteSpace: 'nowrap'
        },
      },
    },
    '& .MuiTablePagination-select': {
      lineHeight: '17px',
    },
  }
}));


const FORMAT_DATE = 'MMM dd, yyyy';
const FORMAT_TIME = 'HH:mm:ss';
const FORMAT_TIME_RECORD = 'HH:mm';


export default function UserHistory() {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { user } = React.useContext(UserContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const { userId } = useParams();
  const requestedUser = useUser(userId, false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [title, setTitle] = React.useState();
  const [historyRows, setHistoryRows] = React.useState([]);
  const titleBase = 'History';


  const columnDefs = [{
    field: 'dateAdded',
    headerName: 'Date',
    minWidth: isMobile ? 70 : 160,
    flex: isMobile ? 70 : 90,
    cellClassName: 'date',
    valueGetter: params => parseISO(params.value),
    renderCell: params => {
      const date = format(params.value, FORMAT_DATE);
      const time = format(params.value, FORMAT_TIME);
      return (
        <div className='date-wrap'>
          <span className='no-wrap'>{date}</span>
          {!isMobile &&
          <>
            &ensp;
            <span className='no-wrap'>{time}</span>
          </>}
        </div>
      );
    }
  }, 
  {
    field: 'actingUser',
    headerName: 'User',
    minWidth: isMobile ? 50 : 70,
    flex: isMobile ? 50 : 60,
    valueGetter: params => params.value.id === user.id ? 'Me' : params.value.name,
    renderCell: params => (
      <Tooltip 
        arrow
        title={params.value}
        children={<span>{params.value}</span>}
      />
    )
  }, 
  {
    field: 'appType',
    headerName: 'Source',
    minWidth: 55,
    flex: 55,
    valueGetter: params => isMobile && params.value === 'Website'
      ? 'Web'
      : params.value
  }, {
    field: 'action',
    headerName: 'Action',
    minWidth: 58,
    flex: 58,
  }, {
    field: 'targetType',
    headerName: isMobile ? 'Record' : 'Record Type',
    minWidth: 60,
    flex: 60,
    valueGetter: params => params.value ?? ''
  }, {
    field: 'targetDate',
    headerName: 'Record Date',
    valueGetter: params => params.value ? parseISO(params.value) : null,
    valueFormatter: params => params.value
      ? `${format(params.value, FORMAT_DATE)} ${format(params.value, FORMAT_TIME_RECORD)}`
      : '',
    minWidth: 120,
    flex: 70,
  }, {
    field: 'targetTitle',
    headerName: isMobile ? ' ' : 'Record Info',
    cellClassName: isMobile? 'flex-hc icon-link' : '',
    minWidth: isMobile ? 40 : 120,
    flex: isMobile ? 40 : 120,
    renderCell: params => {
      const { targetId, targetType, targetTitle, isDeleted, targetParentId } = params.row;
      const trainingPath = '/records';
      let path;
      switch (targetType) {
        case 'Event':
          path = makeLocation(trainingPath, 'training', 'viewEvent', { eventId: targetId }, null);
          break;
        case 'Exercise':
          path = makeLocation(trainingPath, 'training', 'viewExercise', { exerciseId: targetId }, null);
          break;
        case 'Completion':
          path = makeLocation(trainingPath, 'training', 'viewAnswer', { exerciseId: targetParentId, answerId: targetId }, null);
          break;
        case 'Deployment':
          path = makeLocation(trainingPath, 'deployment', 'view', { deploymentId: targetId }, null);
          break;
        case 'Class':
          path = makeLocation(trainingPath, 'class', 'view', { classId: targetId }, null);
          break;
        case 'Dog':
          path = `/dogs/${targetId}/edit/details`;
          break;
        case 'Group':
          path = `/groups/training/${targetId}`;
          break;
        default:
          path = null;
          break;
      }

      if (!path && !targetTitle) {
        return null;
      } else if (isMobile) {
        return <>
          {!isDeleted && path && 
          <Link
            component={RouterLink}
            to={path}
            children={<Icon type="open-link" />}
            underline="hover" />}
        </>;
      } else {
        return (
          <Tooltip 
            arrow
            title={targetTitle 
              ? <>
                  {<>{targetTitle}<br/></>}
                  {isDeleted && <span>(This record no longer exists)</span>}
                  {!isDeleted && path && <span>(Click to open this <>{targetType.toLowerCase()}</>)</span>}
              </> 
              : ''}
            children={!isDeleted && path 
              ? <Link component={RouterLink} to={path} underline="hover">{targetTitle}</Link> 
              : <span>{targetTitle ?? ''}</span>}
          />
        );
      }
    }
  }];


  React.useEffect(() => {
    document.title = `PACKTRACK - ${titleBase}`;
    const title = <PageTitleForUser title={titleBase} userName={requestedUser?.name || ''} />;
    setTitle(title);
    setAppBar(title);
  }, [userId, requestedUser, setAppBar]);

 
  React.useEffect(() => {
    (async() => {
      setIsLoading(true);
      const response = await axios.get('/account/history', { params: { userId }});
      if (response.status === 200) {
        const data = response.data;
        setHistoryRows(data.list);
      }
      setIsLoading(false);
    })();
  }, [userId]);


  return (
    <Container sx={{
      pt: { xs: 0, md: 3, lg: 4 },
      px: { xs: 0, sm: 0, md: 2 },
      height: '100%',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* title */}
      {!isMobile &&
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <PageTitle iconName='history' title={title} />
        </Box>
      }

      <FeatureBox
        loading={isLoading}
        message="Loading history..."
        className={`flex-fh ${classes.grid}`}
        sx={{
          minHeight: 200
        }}
      >
        <DataGrid
          columns={columnDefs}
          rows={historyRows}
          headerHeight={40}
          rowHeight={36}
          disableRowSelectionOnClick
          disableColumnMenu
          initialState={{
            columns: {
              columnVisibilityModel: {
                appType: !isMobile,
                targetDate: !isMobile,
              },
            },
          }}          
        />
      </FeatureBox>
    </Container>
  )
}